import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Dna, Search, History } from "lucide-react";
import HelpBubble from "../components/HelpBubble";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

const VERTICALS = ["finance", "insurance", "legal", "health", "home", "auto", "senior"];

const TABS = [
  { key: "dna", label: "Creative DNA", icon: Dna },
  { key: "keywords", label: "RSOC Keywords", icon: Search },
];

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export default function Intelligence() {
  const [tab, setTab] = useState("dna");
  return (
    <div className="p-6 max-w-[1600px]" data-testid="intelligence-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Intelligence</h1>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          Self-learning creative DNA · RSOC keyword ranking with nightly S1 sync + auto-promotion
        </p>
      </div>

      <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-0.5 mb-4 w-fit">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`intel-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs rounded font-mono transition-colors ${
                tab === t.key ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dna" && <DNAPanel />}
      {tab === "keywords" && <KeywordsPanel />}
    </div>
  );
}

// ---------------- Creative DNA ----------------
function DNAPanel() {
  const [winners, setWinners] = useState({ summary: [], top_winning_dna: {} });
  const [briefs, setBriefs] = useState([]);
  const [briefId, setBriefId] = useState("");
  const [dna, setDna] = useState(null);
  const [testPrompt, setTestPrompt] = useState("Debt relief quiz ad, US middle-aged couple");
  const [enhanced, setEnhanced] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: w }, { data: b }] = await Promise.all([
        api.get("/intelligence/dna/winners"),
        api.get("/ai/briefs"),
      ]);
      setWinners(w);
      setBriefs(b || []);
      if (b?.[0]) setBriefId(b[0].brief_id);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const analyze = async () => {
    if (!briefId) return toast.error("Pick a brief");
    setBusy(true);
    try {
      const { data } = await api.post("/intelligence/dna/analyze", { brief_id: briefId });
      setDna(data.dna);
      toast.success("DNA extracted");
      load();
    } catch (e) { toast.error("Analyze failed"); }
    finally { setBusy(false); }
  };

  const enhancePrompt = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/intelligence/dna/enhance-prompt", { prompt: testPrompt });
      setEnhanced(data);
    } catch { toast.error("Enhance failed"); }
    finally { setBusy(false); }
  };

  const topDna = winners.top_winning_dna || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dna-panel">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2 flex items-center gap-1">Winning DNA (joined against ROI) <HelpBubble feature="dna" size="xs" /></div>
        {Object.keys(topDna).length === 0 ? (
          <div className="text-xs text-slate-500 text-center py-6">No DNA joined yet — click "Analyze" on briefs to start training.</div>
        ) : (
          <div className="grid grid-cols-2 gap-2 mb-3" data-testid="dna-top">
            {Object.entries(topDna).map(([axis, tag]) => (
              <div key={axis} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded p-2.5">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{axis}</div>
                <div className="text-sm text-slate-900 font-medium">{tag}</div>
              </div>
            ))}
          </div>
        )}
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-4 mb-2">Tag ROI leaderboard</div>
        <div className="max-h-[300px] overflow-y-auto border border-slate-200 rounded">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 sticky top-0 text-slate-500 text-[10px] uppercase">
              <tr><th className="text-left p-2">Axis</th><th className="text-left p-2">Tag</th><th className="text-right p-2">Avg ROI</th><th className="text-right p-2">#</th></tr>
            </thead>
            <tbody>
              {winners.summary.slice(0, 20).map((s, i) => (
                <tr key={`${s.axis}-${s.tag}`} className="border-t border-slate-200" data-testid={`dna-row-${i}`}>
                  <td className="p-2 text-slate-500 font-mono">{s.axis}</td>
                  <td className="p-2 text-slate-900">{s.tag}</td>
                  <td className={`p-2 text-right font-mono ${s.avg_roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(s.avg_roi)}</td>
                  <td className="p-2 text-right font-mono text-slate-500">{s.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">Train — analyze a brief's DNA</div>
          <select
            value={briefId}
            onChange={(e) => setBriefId(e.target.value)}
            data-testid="dna-brief-select"
            className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs"
          >
            {briefs.map((b) => <option key={b.brief_id} value={b.brief_id}>{b.title} · {b.vertical}</option>)}
          </select>
          <button onClick={analyze} disabled={busy} data-testid="dna-analyze-btn" className="w-full mt-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-60">
            {busy ? "Analyzing…" : "Extract DNA"}
          </button>
          {dna && (
            <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-[11px] font-mono" data-testid="dna-extracted">
              {Object.entries(dna).map(([k, v]) => (
                <div key={k}><span className="text-slate-500">{k}:</span> <span className="text-slate-900">{v}</span></div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-slate-200">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">Test — bias any prompt toward winning DNA</div>
          <textarea
            value={testPrompt}
            onChange={(e) => setTestPrompt(e.target.value)}
            rows={3}
            data-testid="dna-test-prompt"
            className="w-full bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
          />
          <button onClick={enhancePrompt} disabled={busy} data-testid="dna-enhance-btn" className="w-full mt-2 bg-slate-900 text-white hover:bg-slate-800 rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-60">
            {busy ? "…" : "Enhance with winning DNA"}
          </button>
          {enhanced && (
            <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-[11px]" data-testid="dna-enhanced">
              <div className="text-[10px] uppercase text-emerald-700 font-mono mb-1">Enhanced prompt</div>
              <div className="text-slate-900 font-mono whitespace-pre-wrap">{enhanced.enhanced_prompt}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- Keywords ----------------
function KeywordsPanel() {
  const [vertical, setVertical] = useState("finance");
  const [data, setData] = useState({ keywords: [] });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [promoted, setPromoted] = useState(new Set()); // lowercased keywords
  const [autoCfg, setAutoCfg] = useState({ enabled: false, min_rpc: 3.0, min_clicks: 50 });
  const [savingCfg, setSavingCfg] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState({ events: [], counts: {}, days: 7 });
  const [historyBusy, setHistoryBusy] = useState(false);

  const load = async (v) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/intelligence/keywords?vertical=${v || vertical}`);
      setData(data);
    } finally { setBusy(false); }
  };
  const loadSyncStatus = async () => {
    try {
      const { data } = await api.get("/intelligence/keywords/sync/status");
      setSyncStatus(data);
    } catch { /* non-blocking */ }
  };
  const loadPromoted = async (v) => {
    try {
      const { data } = await api.get(`/intelligence/keywords/promoted?vertical=${v || vertical}`);
      setPromoted(new Set((data.items || []).map((p) => p.keyword.toLowerCase())));
    } catch { /* non-blocking */ }
  };
  const loadAutoCfg = async () => {
    try {
      const { data } = await api.get("/intelligence/keywords/auto-promote");
      setAutoCfg(data);
    } catch { /* non-blocking */ }
  };
  useEffect(() => {
    load(vertical); loadSyncStatus(); loadPromoted(vertical); loadAutoCfg();
    /* eslint-disable-next-line */
  }, [vertical]);

  const saveAutoCfg = async (next) => {
    setSavingCfg(true);
    try {
      const { data } = await api.post("/intelligence/keywords/auto-promote", next);
      setAutoCfg(data.config);
      toast.success(
        next.enabled
          ? `Auto-promotion ON — keywords with RPC ≥ $${next.min_rpc} and clicks ≥ ${next.min_clicks} will auto-promote nightly`
          : "Auto-promotion OFF"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed (admin only)");
    } finally { setSavingCfg(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/intelligence/keywords/sync");
      if (data.synced === 0 && data.reason) {
        // Show the actual reason so the buyer can act (likely: no S1 reporting
        // permission on the auth_key or no traffic in the last N days).
        toast.warning(`No keywords synced: ${data.reason}`, { duration: 8000 });
      } else {
        const apNote = data.auto_promoted ? ` · ★ auto-promoted ${data.auto_promoted}` : "";
        toast.success(`Synced ${data.synced} keywords from System1 (${data.days}d)${apNote}`);
      }
      await loadSyncStatus();
      await loadPromoted(vertical);
      await load(vertical);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed — check your System1 auth key");
    } finally { setSyncing(false); }
  };

  const togglePromote = async (kw) => {
    const isPromoted = promoted.has(kw.keyword.toLowerCase());
    try {
      if (isPromoted) {
        await api.delete(`/intelligence/keywords/promote?vertical=${encodeURIComponent(vertical)}&keyword=${encodeURIComponent(kw.keyword)}`);
        toast.success(`"${kw.keyword}" removed from next launch`);
      } else {
        await api.post("/intelligence/keywords/promote", {
          vertical, keyword: kw.keyword, rpc: kw.rpc,
        });
        toast.success(`"${kw.keyword}" will auto-inject into next Matrix launch`);
      }
      await loadPromoted(vertical);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryBusy(true);
    try {
      const { data } = await api.get("/intelligence/keywords/promotion-history?days=7");
      setHistory(data);
    } catch (e) {
      toast.error("Could not load history");
    } finally { setHistoryBusy(false); }
  };

  const undoEvent = async (ev) => {
    try {
      await api.delete(`/intelligence/keywords/promote?vertical=${encodeURIComponent(ev.vertical)}&keyword=${encodeURIComponent(ev.keyword)}`);
      toast.success(`"${ev.keyword}" demoted — won't inject on next launch`);
      await openHistory();
      await loadPromoted(vertical);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    }
  };

  const lastSyncedLabel = syncStatus?.last_synced_at
    ? new Date(syncStatus.last_synced_at).toLocaleString()
    : "Never";
  const promotedCount = promoted.size;

  return (
    <div className="space-y-4" data-testid="keywords-panel">
      {/* Auto-promotion config */}
      <div className="bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoCfg.enabled}
              onChange={(e) => saveAutoCfg({ ...autoCfg, enabled: e.target.checked })}
              disabled={savingCfg}
              data-testid="kw-auto-promote-toggle"
              className="w-4 h-4 accent-amber-500"
            />
            <span className="text-xs font-semibold text-slate-900">
              Auto-promote nightly
            </span>
          </label>
          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${autoCfg.enabled ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-500"}`}>
            {autoCfg.enabled ? "ON" : "OFF"}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-slate-600 font-mono">
          <span>RPC ≥ $</span>
          <input
            type="number" step="0.1" min="0.1"
            value={autoCfg.min_rpc}
            onChange={(e) => setAutoCfg({ ...autoCfg, min_rpc: parseFloat(e.target.value) || 0 })}
            onBlur={() => autoCfg.enabled && saveAutoCfg(autoCfg)}
            data-testid="kw-auto-promote-rpc"
            className="w-16 bg-white border border-slate-300 rounded px-1.5 py-0.5 text-xs"
          />
          <span className="ml-2">clicks ≥</span>
          <input
            type="number" step="1" min="1"
            value={autoCfg.min_clicks}
            onChange={(e) => setAutoCfg({ ...autoCfg, min_clicks: parseInt(e.target.value) || 0 })}
            onBlur={() => autoCfg.enabled && saveAutoCfg(autoCfg)}
            data-testid="kw-auto-promote-clicks"
            className="w-16 bg-white border border-slate-300 rounded px-1.5 py-0.5 text-xs"
          />
        </div>
        <span className="text-[10px] text-slate-500 font-mono flex-1 min-w-[180px]">
          Any keyword past both thresholds (after the 03:15 UTC sync) is auto-promoted — no clicks required.
          {syncStatus?.auto_promoted ? ` Last sync: ★ ${syncStatus.auto_promoted} auto-promoted.` : ""}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[10px] text-slate-500 font-mono">Vertical</label>
        <select
          value={vertical}
          onChange={(e) => setVertical(e.target.value)}
          data-testid="kw-vertical"
          className="bg-white border border-slate-200 rounded px-2 py-1.5 text-xs"
        >
          {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <HelpBubble feature="keywords" size="xs" />
        {promotedCount > 0 && (
          <span
            data-testid="kw-promoted-count"
            className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300"
            title="These keywords are auto-injected as forceKey on every new campaign brief for this vertical"
          >
            ★ {promotedCount} promoted → next launch
          </span>
        )}
        <div className="flex-1" />
        <div
          className="text-[10px] text-slate-500 font-mono"
          data-testid="kw-last-synced"
          title={`${syncStatus?.total_stored_keywords || 0} keywords in the historical cache`}
        >
          Last S1 sync: <span className="text-slate-900">{lastSyncedLabel}</span>
          {syncStatus?.keywords_updated ? ` · ${syncStatus.keywords_updated} updated` : ""}
        </div>
        <button
          onClick={openHistory}
          data-testid="kw-history-btn"
          title="Show the last 7 days of auto + manual promotions/demotions"
          className="text-[11px] font-mono px-2.5 py-1 rounded bg-white border border-slate-300 text-slate-700 hover:border-amber-400 hover:text-amber-600 flex items-center gap-1"
        >
          <History className="w-3 h-3" /> History
        </button>
        <button
          onClick={syncNow}
          disabled={syncing}
          data-testid="kw-sync-now"
          className="text-[11px] font-mono px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
        >
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr>
              <th className="text-left p-2.5">#</th>
              <th className="text-left p-2.5">Keyword</th>
              <th className="text-right p-2.5">Est RPC</th>
              <th className="text-right p-2.5">Monthly Searches</th>
              <th className="text-right p-2.5">Score</th>
              <th className="text-right p-2.5">Source</th>
              <th className="text-right p-2.5">Action</th>
            </tr>
          </thead>
          <tbody>
            {busy && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>}
            {!busy && data.keywords.map((k, i) => {
              const badge = k.source === "historical"
                ? "bg-emerald-100 text-emerald-700"
                : k.source === "historical-global"
                ? "bg-blue-100 text-blue-700"
                : "bg-slate-100 text-slate-500";
              const isPromoted = promoted.has(k.keyword.toLowerCase());
              const canPromote = k.source === "historical" || k.source === "historical-global";
              return (
                <tr key={k.keyword} data-testid={`kw-row-${i}`} className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="p-2.5 text-slate-500 font-mono">{i + 1}</td>
                  <td className="p-2.5 text-slate-900 font-medium">
                    {isPromoted && <span className="text-amber-500 mr-1" title="Promoted to next Matrix launch">★</span>}
                    {k.keyword}
                  </td>
                  <td className="p-2.5 text-right font-mono text-emerald-600">{fmtMoney(k.rpc)}</td>
                  <td className="p-2.5 text-right font-mono text-slate-600">{Number(k.est_monthly_searches).toLocaleString()}</td>
                  <td className="p-2.5 text-right font-mono text-slate-900 font-semibold">{k.score}</td>
                  <td className="p-2.5 text-right">
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${badge}`}>{k.source}</span>
                  </td>
                  <td className="p-2.5 text-right">
                    {canPromote ? (
                      <button
                        onClick={() => togglePromote(k)}
                        data-testid={`kw-promote-${i}`}
                        className={`text-[10px] font-mono px-2 py-0.5 rounded border transition-colors ${
                          isPromoted
                            ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600"
                            : "bg-white border-slate-300 text-slate-600 hover:border-amber-400 hover:text-amber-600"
                        }`}
                        title={isPromoted
                          ? "Promoted — auto-injected as forceKey on next Matrix launch. Click to remove."
                          : "Promote to real bid — auto-injected into the next campaign brief for this vertical."}
                      >
                        {isPromoted ? "★ Promoted" : "Promote"}
                      </button>
                    ) : (
                      <span className="text-[10px] text-slate-300 font-mono">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 font-mono">
        Sorted by RPC × √searches. <span className="text-emerald-700">historical</span> = real RSOC data for this vertical ·
        <span className="text-blue-700"> historical-global</span> = real RSOC data across other verticals (fallback) ·
        <span className="text-slate-500"> estimated</span> = deterministic seed until first S1 report hits.
        Sync runs nightly at 03:15 UTC. <span className="text-amber-600">★ Promoted</span> keywords are auto-prepended to every new brief's forceKey slots for this vertical. Enable auto-promotion above to skip the manual click.
      </p>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="right" className="w-[480px] sm:max-w-[520px] bg-white overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-slate-900 flex items-center gap-2">
              <History className="w-4 h-4 text-amber-500" /> Promotion history
              <span className="text-[10px] font-mono text-slate-500 font-normal">last 7 days</span>
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex gap-2 text-[10px] font-mono flex-wrap">
            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300">
              ★ auto-promote: {history.counts?.auto_promote || 0}
            </span>
            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-300">
              manual promote: {history.counts?.manual_promote || 0}
            </span>
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-300">
              demoted: {history.counts?.demote || 0}
            </span>
          </div>
          <div className="mt-4 space-y-2" data-testid="kw-history-list">
            {historyBusy && <div className="text-xs text-slate-500">Loading…</div>}
            {!historyBusy && (history.events || []).length === 0 && (
              <div className="text-xs text-slate-500 italic p-3 bg-slate-50 border border-slate-200 rounded">
                No promotion events yet. Enable auto-promote or click Promote on a historical keyword row.
              </div>
            )}
            {!historyBusy && (history.events || []).map((ev, i) => {
              const isAuto = ev.action === "auto_promote";
              const isDemote = ev.action === "demote";
              const color = isDemote
                ? "border-slate-200 bg-slate-50"
                : isAuto
                ? "border-amber-200 bg-amber-50"
                : "border-blue-200 bg-blue-50";
              return (
                <div
                  key={`${ev.ts || ""}-${ev.keyword || ""}-${i}`}
                  data-testid={`kw-history-event-${i}`}
                  className={`border rounded-md p-2.5 text-xs ${color}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                          isDemote ? "bg-slate-200 text-slate-600"
                          : isAuto ? "bg-amber-500 text-white"
                          : "bg-blue-600 text-white"
                        }`}>
                          {isDemote ? "DEMOTED" : isAuto ? "★ AUTO" : "MANUAL"}
                        </span>
                        <span className="text-[10px] font-mono text-slate-500">{ev.vertical}</span>
                      </div>
                      <div className="text-slate-900 font-medium truncate" title={ev.keyword}>
                        {ev.keyword}
                      </div>
                      <div className="text-[10px] font-mono text-slate-500 flex gap-2 mt-0.5 flex-wrap">
                        {ev.rpc !== undefined && <span>rpc ${Number(ev.rpc).toFixed(2)}</span>}
                        {ev.clicks !== undefined && <span>· clicks {ev.clicks}</span>}
                        {ev.revenue !== undefined && <span>· rev ${Number(ev.revenue).toFixed(0)}</span>}
                        <span>· {new Date(ev.at).toLocaleString()}</span>
                      </div>
                    </div>
                    {!isDemote && ev.still_active && (
                      <button
                        onClick={() => undoEvent(ev)}
                        data-testid={`kw-history-undo-${i}`}
                        className="text-[10px] font-mono px-2 py-1 rounded bg-white border border-slate-300 text-slate-700 hover:border-red-400 hover:text-red-600 whitespace-nowrap"
                        title="Remove this keyword from promotions — it won't inject on the next launch"
                      >
                        Undo
                      </button>
                    )}
                    {!isDemote && !ev.still_active && (
                      <span className="text-[10px] font-mono text-slate-400">removed</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
